/**
 * 盒子端商业化上报：window.raw.baiduBusinessUpload
 * 业务统一经此方法调用，便于复用与打日志。
 */

import { getLocalStorage } from '@/utils/localStorage';
import md5 from 'js-md5';
import sdk from '@/utils/Buryingpoint';
import { LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_360LK, REPORTED_KEY } from './types';
import { getReportHisIdFromSeo } from './promoLanding';

export type BaiduBusinessNewTypePayload = { newType: number };
export type BaiduBusinessIndustrValue = 'ocpc_ps_convert' | 'ocpc_zs_convert';
export type BaiduBusinessParamPayload = {
  trans_id?: string;
  event_param?: string | Record<string, unknown> | null;
};
export type BaiduBusinessInfoPayload = {
  businessInfo: string;
  businessType: string;
  industr?: BaiduBusinessIndustrValue;
  businessParam?: string | BaiduBusinessParamPayload | null;
};

export type BaiduBusinessUploadPayload = BaiduBusinessNewTypePayload | BaiduBusinessInfoPayload;
const INDUSTRY_DISPLAY_AD: BaiduBusinessIndustrValue = 'ocpc_zs_convert';
const INDUSTRY_PC_SEARCH: BaiduBusinessIndustrValue = 'ocpc_ps_convert';

export function isBaiduBusinessUploadAvailable(): boolean {
  return Boolean((window as any).raw?.baiduBusinessUpload);
}

export interface CallBaiduBusinessUploadOptions {
  /** 控制台标识，默认从 payload 推断 */
  logLabel?: string;
  /** 合并进 console.log 的额外字段 */
  logExtra?: Record<string, unknown>;
  /** 为 true 则不上报前的 console.log（仍会上报） */
  silentLog?: boolean;
  /** 上传成功埋点 PC_mailiangshujushangbao 的 user.id（仅 360 买量时上报），缺省则从回调 params 里取 outeruserid */
  buryUid?: string | number;
}

function resolveBaiduUploadBuryUid(e: any, options?: CallBaiduBusinessUploadOptions): string {
  if (options?.buryUid != null && String(options.buryUid).length > 0) {
    return String(options.buryUid);
  }
  const ep = e?.params?.data?.data_detail?.event_param;
  if (ep && typeof ep === 'object' && 'outeruserid' in ep) {
    const v = (ep as Record<string, unknown>).outeruserid;
    if (v != null && String(v).length > 0) return String(v);
  }
  return '0';
}

/** 登录注册等场景传入 callBaiduBusinessUpload 第二参，便于成功埋点带真实 uid */
export function buryOptsForUid(buryUid?: string | number): CallBaiduBusinessUploadOptions | undefined {
  if (buryUid == null || String(buryUid).length === 0) return undefined;
  return { buryUid };
}

/** 与 reportBaiduPaySuccessFor360lk 一致：仅 360 买量渠道（loginTrafficSource === 360lk） */
function is360AcquisitionTraffic(): boolean {
  return getLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY) === LOGIN_SOURCE_360LK;
}

/**
 * @returns 是否已成功调用（存在 raw.baiduBusinessUpload）
 */
export function callBaiduBusinessUpload(
  payload: BaiduBusinessUploadPayload,
  options?: CallBaiduBusinessUploadOptions
): boolean {
  const upload = (window as any).raw?.baiduBusinessUpload;
  if (!upload) return false;

  if (!options?.silentLog) {
    const label =
      options?.logLabel ??
      ('businessInfo' in payload
        ? payload.businessInfo
        : `newType:${(payload as BaiduBusinessNewTypePayload).newType}`);
    const logBody =
      options?.logExtra && Object.keys(options.logExtra).length > 0 ? { ...payload, ...options.logExtra } : payload;
    console.log('[baiduBusinessUpload]', label, logBody);
    // 注册上报：打印调用 raw.baiduBusinessUpload 的实际传参，便于联调核对
    if ('businessInfo' in payload && payload.businessInfo === 'REGISTERED') {
      console.log('[baiduBusinessUpload] 注册上报传参', {
        payload,
        buryUid: options?.buryUid,
        logExtra: options?.logExtra
      });
    }
  }

  upload(payload, (e: any) => {
    console.log(e);
    if (e?.status !== 'resolve') return;
    if (!is360AcquisitionTraffic()) return;
    sdk.Burying({
      uid: resolveBaiduUploadBuryUid(e, options),
      eventname: 'PC_mailiangshujushangbao',
      eventtype: 'track',
      properities: {
        ct: e?.params?.data?.request_time,
        data_industry: e?.params?.data?.data_industry,
        params: e?.params
      }
    });
  });
  return true;
}

/** PAY_SUCCESS 的 event_param：订单金额（分）、用户唯一标识（字符串），均为非必填 */
export type BaiduPaySuccessEventParamOptions = {
  /** 订单金额，单位：分 */
  value?: number;
  /** 用户唯一标识 */
  outeruserid?: string;
};

function buildPaySuccessEventParam(options?: BaiduPaySuccessEventParamOptions): Record<string, unknown> | null {
  if (!options) return null;
  const payload: Record<string, string | number> = {};
  if (typeof options.value === 'number' && !Number.isNaN(options.value)) {
    payload.value = options.value;
  }
  if (options.outeruserid != null && String(options.outeruserid).length > 0) {
    payload.outeruserid = String(options.outeruserid);
  }
  if (Object.keys(payload).length === 0) return null;
  return payload;
}

function isSatPczsSourceId(sourceId: string): boolean {
  return sourceId.startsWith('SATpczs');
}

function resolvePaySuccessIndustr(): BaiduBusinessIndustrValue {
  const currentHisId = getReportHisIdFromSeo(getLocalStorage(REPORTED_KEY) || '');
  if (!currentHisId) return INDUSTRY_PC_SEARCH;
  const paramArr = currentHisId.split('#');
  if (!isSatPczsSourceId(paramArr[0] || '')) return INDUSTRY_PC_SEARCH;
  const hasJzqs = paramArr.length >= 5 && Boolean(paramArr[3]);
  return hasJzqs ? INDUSTRY_DISPLAY_AD : INDUSTRY_PC_SEARCH;
}

/**
 * 360 流量（loginTrafficSource === 360lk）且客户端支持时上报 PAY_SUCCESS（扫码支付成功等场景复用）。
 * event_param 中可含 value（分）、outeruserid（字符串），见百度转化数据格式。
 * @returns 是否已调用 raw.baiduBusinessUpload
 */
export function reportBaiduPaySuccessFor360lk(
  payOrder: string,
  eventOptions?: BaiduPaySuccessEventParamOptions
): boolean {
  const loginTrafficSource = getLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY);
  if (loginTrafficSource !== LOGIN_SOURCE_360LK || !isBaiduBusinessUploadAvailable()) {
    return false;
  }
  const eventParam = buildPaySuccessEventParam(eventOptions);
  const industr = resolvePaySuccessIndustr();
  const businessParam: BaiduBusinessParamPayload = {
    trans_id: md5(String(Date.now())),
    event_param: eventParam
  };
  return callBaiduBusinessUpload(
    { businessInfo: 'PAY', businessType: '1', industr, businessParam },
    { logLabel: `PAY:${payOrder}`, buryUid: eventOptions?.outeruserid }
  );
}
